import React, { useEffect, useState } from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Batch, Recipe, BatchAnalysis, BatchSchedule } from '@/types';
import { BatchService } from '@/services/BatchService';
import { RecipeService } from '@/services/RecipeService';
import { useAuth } from '@/context/AuthContext';
import { useCredits } from '@/context/CreditsContext';
import { useI18n } from '@/i18n/I18nContext';
import { getBrewfatherCredentials } from '@/services/BrewfatherService';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IconButton from '@mui/material/IconButton';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import PsychologyIcon from '@mui/icons-material/Psychology';
import Button from '@mui/material/Button';
import AIChatOverlay, { ChatMessage, AI_CHAT_USE_FOOTER, AI_CHAT_FOOTER_BAR_HEIGHT } from '@/components/AIChatOverlay';
import CreditsDisplay from '@/components/CreditsDisplay';
import LinkIcon from '@mui/icons-material/Link';
import { AIService } from '@/services/AIService';
import EventIcon from '@mui/icons-material/Event';
import NotesIcon from '@mui/icons-material/Notes';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Tooltip from '@mui/material/Tooltip';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AlertIcon from '@mui/icons-material/Info';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveIcon from '@mui/icons-material/Remove';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import DeleteIcon from '@mui/icons-material/Delete';

/**
 * Get status-appropriate suggested questions for the AI chat based on batch status
 */
const getSuggestedQuestionsForStatus = (status: Batch['status']): string[] => {
	switch (status) {
		case 'planned':
			return [
				'What should I prepare before brewing this batch?',
				'What equipment do I need for this recipe?',
				'What are the key steps I should follow?',
				'What should I expect during the brew day?',
				'Are there any special considerations for this recipe?'
			];
		case 'brewing':
			return [
				'How is my brew day progressing?',
				'Are my mash temperatures correct?',
				'What should I watch for during the boil?',
				'Is my hop schedule appropriate?',
				'How do my current measurements compare to the recipe?'
			];
		case 'fermenting':
			return [
				'How is my fermentation progressing?',
				'What does my current gravity reading tell me?',
				'Is my fermentation temperature appropriate?',
				'What should I do if fermentation seems stuck?',
				'How long until fermentation should be complete?',
				'Are there any issues with my current temperature?',
				'What do the latest device readings indicate?',
				'Should I adjust anything based on the current readings?'
			];
		case 'conditioning':
			return [
				'How is my beer conditioning progressing?',
				'Is my beer ready to drink?',
				'How long should I continue conditioning?',
				'What should I look for to know conditioning is complete?',
				'Are there any issues with the conditioning process?',
				'What storage conditions are best for conditioning?',
				'How does the current gravity reading relate to conditioning?',
				'Should I adjust anything during conditioning?'
			];
		case 'completed':
			return [
				'How did this batch turn out?',
				'What were the key learnings from this batch?',
				'How does the final beer compare to the recipe?',
				'What would I do differently next time?',
				'What were the highlights of this batch?'
			];
		case 'archived':
			return [
				'What were the key details of this batch?',
				'How did this batch compare to the recipe?',
				'What were the final results?',
				'What did I learn from this batch?'
			];
		default:
			return [
				'How is my batch progressing?',
				'What should I know about the current status?',
				'Are there any issues I should be aware of?'
			];
	}
};

const BatchDetailPage: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { user, getAuthHeader, getUserId } = useAuth();
	const { setCreditsLoading, loadCredits } = useCredits();
	const { t } = useI18n();
	const [batch, setBatch] = useState<Batch | null>(null);
	const [recipe, setRecipe] = useState<Recipe | null>(null);
	const [loading, setLoading] = useState(true);
	const [analyzing, setAnalyzing] = useState(false);
	const [showAllReadings, setShowAllReadings] = useState(false);
	const [aiReview, setAiReview] = useState<{
		summary: string;
		recommendations?: string[];
		changes?: Array<{
			field: string;
			oldValue: any;
			newValue: any;
			reason: string;
		}>;
	} | null>(null);
	const [analyses, setAnalyses] = useState<BatchAnalysis[]>([]);
	const [analysesLoading, setAnalysesLoading] = useState(false);
	const [expandedAnalysisId, setExpandedAnalysisId] = useState<string | null>(null);
	const [showAllAnalyses, setShowAllAnalyses] = useState(false);
	const AI_ANALYSES_VISIBLE = 3;
	const displayedAnalyses = showAllAnalyses ? analyses : analyses.slice(0, AI_ANALYSES_VISIBLE);
	const hasMoreAnalyses = analyses.length > AI_ANALYSES_VISIBLE;
	const [schedule, setSchedule] = useState<BatchSchedule | null>(null);
	const [scheduleLoading, setScheduleLoading] = useState(false);
	const [scheduleSaving, setScheduleSaving] = useState(false);
	const [scheduleInterval, setScheduleInterval] = useState<number>(1); // Default: 1 day
	const [scheduleIntervalUnit, setScheduleIntervalUnit] = useState<'hours' | 'days'>('days');
	const [scheduleEnabled, setScheduleEnabled] = useState(true);

	useEffect(() => {
		if (!user || !id) {
			navigate('/batches');
			return;
		}
		loadBatch();
	}, [id, user]);

	useEffect(() => {
		if (!user || !id) return;
		const userId = getUserId();
		if (!userId) return;
		let cancelled = false;
		(async () => {
			try {
				setAnalysesLoading(true);
				const authHeader = getAuthHeader();
				const res = await BatchService.listAnalyses(id!, userId, authHeader);
				if (!cancelled) setAnalyses(res.items || []);
			} catch (error: any) {
				if (!cancelled) {
					console.warn('Could not load analyses:', error);
					setAnalyses([]);
				}
			} finally {
				if (!cancelled) setAnalysesLoading(false);
			}
		})();
		return () => { cancelled = true; };
	}, [id, user]);

	useEffect(() => {
		if (!user || !id) return;
		const userId = getUserId();
		if (!userId) return;
		let cancelled = false;
		(async () => {
			try {
				setScheduleLoading(true);
				const authHeader = getAuthHeader();
				const res = await BatchService.getSchedule(id!, userId, authHeader);
				if (!cancelled) {
					setSchedule(res.schedule);
					if (res.schedule) {
						const interval = res.schedule.interval;
						if (interval >= 1440) {
							setScheduleIntervalUnit('days');
							setScheduleInterval(Math.min(24, Math.max(1, Math.floor(interval / 1440))));
						} else {
							setScheduleIntervalUnit('hours');
							setScheduleInterval(Math.min(24, Math.max(1, Math.floor(interval / 60))));
						}
						setScheduleEnabled(res.schedule.enabled);
					}
				}
			} catch (error: any) {
				if (!cancelled) {
					console.warn('Could not load schedule:', error);
					setSchedule(null);
				}
			} finally {
				if (!cancelled) setScheduleLoading(false);
			}
		})();
		return () => { cancelled = true; };
	}, [id, user]);

	const loadBatch = async () => {
		try {
			setLoading(true);
			const userId = getUserId();
			if (!userId) {
				navigate('/batches');
				return;
			}
			const authHeader = getAuthHeader();
			const credentials = await getBrewfatherCredentials(userId, authHeader);
			if (!credentials) {
				navigate('/batches');
				return;
			}
			const response = await BatchService.getBatch(id!, userId, authHeader);
			setBatch(response.item);
			
			// Fetch recipe details if recipeId is available
			if (response.item.recipeId) {
				try {
					const recipeResponse = await RecipeService.getRecipe(response.item.recipeId, userId, authHeader);
					setRecipe(recipeResponse.item);
				} catch (error) {
					console.warn('Could not fetch recipe details:', error);
					// Don't fail the whole page if recipe fetch fails
				}
			}
		} catch (error: any) {
			console.error('Error loading batch:', error);
			navigate('/batches');
		} finally {
			setLoading(false);
		}
	};

	const calculatePercentageDiff = (measured: number | null | undefined, estimated: number | null | undefined): number | null => {
		if (measured === undefined || measured === null || estimated === undefined || estimated === null || estimated === 0) return null;
		return ((measured - estimated) / estimated) * 100;
	};

	const isNumber = (value: number | null | undefined): value is number => {
		return value !== null && value !== undefined && typeof value === 'number';
	};

	const formatPercentage = (value: number | null): string => {
		if (value === null) return '';
		const sign = value >= 0 ? '+' : '';
		return `${sign}${value.toFixed(1)}%`;
	};

	const formatEventDate = (timestamp: number): string => {
		return new Date(timestamp).toLocaleDateString();
	};

	const calculateDaysBetween = (startDate: number | string | undefined, endDate?: number): number | null => {
		if (!startDate) return null;
		const start = typeof startDate === 'string' ? new Date(startDate).getTime() : startDate;
		const end = endDate || Date.now();
		if (isNaN(start) || isNaN(end)) return null;
		const diffMs = end - start;
		const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
		return diffDays;
	};

	const calculateDaysFermenting = (fermentationStartDate?: number): number | null => {
		return calculateDaysBetween(fermentationStartDate);
	};

	const calculateDaysSinceBrew = (brewDate?: string | number): number | null => {
		return calculateDaysBetween(brewDate);
	};

	const calculateDaysUntilBottling = (bottlingDate?: string | number): number | null => {
		if (!bottlingDate) return null;
		const bottling = typeof bottlingDate === 'string' ? new Date(bottlingDate).getTime() : bottlingDate;
		const now = Date.now();
		if (isNaN(bottling)) return null;
		const diffMs = bottling - now;
		const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
		return diffDays;
	};

	const calculateDaysRemaining = (daysFermenting: number | null, expectedDays?: number): number | null => {
		if (daysFermenting === null || expectedDays === undefined) return null;
		return Math.max(0, expectedDays - daysFermenting);
	};

	const getTrendIndicator = (current: number | null | undefined, previous: number | null | undefined, lowerIsBetter: boolean = false): { icon: React.ReactNode; color: string } | null => {
		if (current === undefined || current === null || previous === undefined || previous === null) return null;
		const diff = current - previous;
		if (Math.abs(diff) < 0.001) {
			return { icon: <RemoveIcon fontSize="small" />, color: 'text.secondary' };
		}
		const isPositive = lowerIsBetter ? diff < 0 : diff > 0;
		if (isPositive) {
			return { icon: <ArrowUpwardIcon fontSize="small" />, color: lowerIsBetter ? 'success.main' : 'success.main' };
		} else {
			return { icon: <ArrowDownwardIcon fontSize="small" />, color: lowerIsBetter ? 'warning.main' : 'warning.main' };
		}
	};

	const isValidNumber = (value: number | null | undefined): value is number => {
		return value !== null && value !== undefined && typeof value === 'number' && !isNaN(value);
	};

	const handleRunAIReview = async () => {
		if (!batch || !id) return;
		
		try {
			setAnalyzing(true);
			setCreditsLoading(true);
			const userId = getUserId();
			if (!userId) {
				toast.error('Please log in to run AI review');
				return;
			}
			const authHeader = getAuthHeader();
			const response = await BatchService.analyzeBatch(id, userId, authHeader);
			
			setAiReview({
				summary: response.summary,
				recommendations: response.recommendations || [],
				changes: response.changes || []
			});
			// Prepend new analysis to list so progress is visible without refetch
			if (response.analysisId && response.createdAt) {
				setAnalyses((prev) => [
					{
						analysisId: response.analysisId!,
						entityType: 'batch',
						entityId: id!,
						summary: response.summary,
						recommendations: response.recommendations || [],
						changes: response.changes || [],
						createdAt: response.createdAt!,
						batchName: batch.name
					},
					...prev
				]);
			} else {
				// Fallback: refetch list
				const res = await BatchService.listAnalyses(id, userId, authHeader);
				setAnalyses(res.items || []);
			}
			
			toast.success('AI review completed successfully');
		} catch (error: any) {
			console.error('Error running AI review:', error);
			toast.error(error.message || 'Failed to run AI review');
		} finally {
			setAnalyzing(false);
			setCreditsLoading(false);
			await loadCredits();
		}
	};

	const handleSaveSchedule = async () => {
		if (!id) return;
		
		try {
			setScheduleSaving(true);
			const userId = getUserId();
			if (!userId) {
				toast.error('Please log in to manage schedule');
				return;
			}
			const authHeader = getAuthHeader();
			
			// Convert interval to minutes
			const intervalMinutes = scheduleIntervalUnit === 'hours' 
				? Math.min(24, Math.max(1, scheduleInterval)) * 60 
				: Math.min(24, Math.max(1, scheduleInterval)) * 1440;

			if (schedule) {
				// Update existing schedule
				await BatchService.updateSchedule(id, userId, intervalMinutes, scheduleEnabled, authHeader);
				toast.success('Schedule updated successfully');
			} else {
				// Create new schedule
				const res = await BatchService.createSchedule(id, userId, intervalMinutes, scheduleEnabled, authHeader);
				setSchedule(res.schedule);
				toast.success('Schedule created successfully');
			}
			
			// Reload schedule to get updated data
			const res = await BatchService.getSchedule(id, userId, authHeader);
			setSchedule(res.schedule);
		} catch (error: any) {
			console.error('Error saving schedule:', error);
			toast.error(error.message || 'Failed to save schedule');
		} finally {
			setScheduleSaving(false);
		}
	};

	const handleDeleteSchedule = async () => {
		if (!id || !schedule) return;
		
		if (!window.confirm('Are you sure you want to delete this schedule? Automatic AI analyses will stop.')) {
			return;
		}
		
		try {
			setScheduleSaving(true);
			const userId = getUserId();
			if (!userId) {
				toast.error('Please log in to manage schedule');
				return;
			}
			const authHeader = getAuthHeader();
			await BatchService.deleteSchedule(id, userId, authHeader);
			setSchedule(null);
			toast.success('Schedule deleted successfully');
		} catch (error: any) {
			console.error('Error deleting schedule:', error);
			toast.error(error.message || 'Failed to delete schedule');
		} finally {
			setScheduleSaving(false);
		}
	};

	if (loading) {
		return (
			<Container sx={{ py: 8, textAlign: 'center' }}>
				<CircularProgress />
			</Container>
		);
	}

	if (!batch) {
		return (
			<Container sx={{ py: 8, textAlign: 'center' }}>
				<Typography variant="h6" color="text.secondary">
					{t.batches.batchNotFound}
				</Typography>
				<Button onClick={() => navigate('/batches')} sx={{ mt: 2 }}>
					{t.batches.backToBatches}
				</Button>
			</Container>
		);
	}

	const statusLabel = t.batches.status[batch.status] || batch.status;
	const daysFermenting = calculateDaysFermenting(batch.fermentationStartDate);
	const daysSinceBrew = calculateDaysSinceBrew(batch.brewDate);
	const daysUntilBottling = calculateDaysUntilBottling(batch.bottlingDate);
	const daysRemaining = calculateDaysRemaining(daysFermenting, batch.expectedFermentationDays);

	return (
		<Container
			maxWidth="lg"
			sx={{
				py: 4,
				...(AI_CHAT_USE_FOOTER && { pb: AI_CHAT_FOOTER_BAR_HEIGHT + 24 })
			}}
		>
			<ToastContainer position="top-right" />
			<Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
				<IconButton onClick={() => navigate('/batches')}>
					<ArrowBackIcon />
				</IconButton>
				<Typography variant="h4" fontWeight={800}>
					{batch.name}
				</Typography>
				<Chip
					label={statusLabel}
					color={
						batch.status === 'completed' ? 'success' :
						batch.status === 'brewing' || batch.status === 'fermenting' ? 'primary' :
						batch.status === 'conditioning' ? 'secondary' :
						'default'
					}
				/>
				{analyses.length > 0 && (
					<Tooltip title="AI reviews for this batch – see list below">
						<Chip
							icon={<AutoAwesomeIcon sx={{ fontSize: 18 }} />}
							label={analyses.length === 1 ? '1 AI review' : `${analyses.length} AI reviews`}
							variant="outlined"
							size="small"
						/>
					</Tooltip>
				)}
				{analyses.length > 0 && analyses[0]?.createdAt && (
					<Typography variant="caption" color="text.secondary">
						Last review: {new Date(analyses[0].createdAt).toLocaleDateString()}
					</Typography>
				)}
			</Box>

			<Grid container spacing={3}>
				<Grid item xs={12} md={8}>
					<Paper sx={{ p: 3, mb: 3 }}>
						<Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
							{t.batches.title}
						</Typography>
						<Grid container spacing={2}>
							{batch.recipeName && (
								<Grid item xs={12}>
									<Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
										<Stack spacing={2}>
											<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
													<LocalBarIcon color="primary" />
													<Typography variant="caption" color="text.secondary">
														{t.batches.recipe}
													</Typography>
												</Box>
												{batch.recipeId && (
													<Button
														component={Link}
														to={`/recipes/${batch.recipeId}`}
														size="small"
														variant="outlined"
														startIcon={<LinkIcon />}
													>
														View Recipe
													</Button>
												)}
											</Box>
											<Box>
												{batch.recipeId ? (
													<Typography
														component={Link}
														to={`/recipes/${batch.recipeId}`}
														variant="h6"
														fontWeight={700}
														sx={{
															color: 'primary.main',
															textDecoration: 'none',
															'&:hover': {
																textDecoration: 'underline'
															}
														}}
													>
														{batch.recipeName}
													</Typography>
												) : (
													<Typography variant="h6" fontWeight={700}>
														{batch.recipeName}
													</Typography>
												)}
											</Box>
											{recipe && (
												<Grid container spacing={2} sx={{ mt: 1 }}>
													{isNumber(recipe.originalGravity) && (
														<Grid item xs={6} sm={4}>
															<Typography variant="caption" color="text.secondary">
																Recipe OG
															</Typography>
															<Typography variant="body2" fontWeight={600}>
																{recipe.originalGravity.toFixed(3)}
															</Typography>
														</Grid>
													)}
													{isNumber(recipe.finalGravity) && (
														<Grid item xs={6} sm={4}>
															<Typography variant="caption" color="text.secondary">
																Recipe FG
															</Typography>
															<Typography variant="body2" fontWeight={600}>
																{recipe.finalGravity.toFixed(3)}
															</Typography>
														</Grid>
													)}
													{isNumber(recipe.abv) && (
														<Grid item xs={6} sm={4}>
															<Typography variant="caption" color="text.secondary">
																Recipe ABV
															</Typography>
															<Typography variant="body2" fontWeight={600}>
																{recipe.abv.toFixed(1)}%
															</Typography>
														</Grid>
													)}
													{isNumber(recipe.ibu) && (
														<Grid item xs={6} sm={4}>
															<Typography variant="caption" color="text.secondary">
																Recipe IBU
															</Typography>
															<Typography variant="body2" fontWeight={600}>
																{recipe.ibu}
															</Typography>
														</Grid>
													)}
													{isNumber(recipe.srm) && (
														<Grid item xs={6} sm={4}>
															<Typography variant="caption" color="text.secondary">
																Recipe SRM
															</Typography>
															<Typography variant="body2" fontWeight={600}>
																{recipe.srm.toFixed(1)}
															</Typography>
														</Grid>
													)}
													{recipe.style && (
														<Grid item xs={6} sm={4}>
															<Typography variant="caption" color="text.secondary">
																Style
															</Typography>
															<Typography variant="body2" fontWeight={600}>
																{recipe.style}
															</Typography>
														</Grid>
													)}
												</Grid>
											)}
										</Stack>
									</Paper>
								</Grid>
							)}
							{batch.brewDate && (
								<Grid item xs={12} sm={6}>
									<Stack direction="row" spacing={1} alignItems="center">
										<CalendarTodayIcon color="action" />
										<Box>
											<Typography variant="caption" color="text.secondary">
												{t.batches.brewDate}
											</Typography>
											<Typography variant="body1" fontWeight={600}>
												{batch.brewDate}
											</Typography>
											{daysSinceBrew !== null && (
												<Typography variant="caption" color="text.secondary">
													{daysSinceBrew} {daysSinceBrew === 1 ? t.batches.day : t.batches.days} {t.batches.ago}
												</Typography>
											)}
										</Box>
									</Stack>
								</Grid>
							)}
							{batch.bottlingDate && (
								<Grid item xs={12} sm={6}>
									<Stack direction="row" spacing={1} alignItems="center">
										<CalendarTodayIcon color="action" />
										<Box>
											<Typography variant="caption" color="text.secondary">
												{t.batches.bottlingDate}
											</Typography>
											<Typography variant="body1" fontWeight={600}>
												{batch.bottlingDate}
											</Typography>
											{daysUntilBottling !== null && (
												<Typography variant="caption" color={daysUntilBottling <= 0 ? 'success.main' : 'text.secondary'}>
													{daysUntilBottling <= 0 
														? t.batches.overdue
														: daysUntilBottling === 0
														? t.batches.today
														: `${daysUntilBottling} ${daysUntilBottling === 1 ? t.batches.day : t.batches.days} ${t.batches.remaining}`
													}
												</Typography>
											)}
										</Box>
									</Stack>
								</Grid>
							)}
							{(daysFermenting !== null || batch.expectedFermentationDays !== undefined) && (
								<Grid item xs={12} sm={6}>
									<Stack direction="row" spacing={1} alignItems="center">
										<CalendarTodayIcon color="action" />
										<Box>
											<Typography variant="caption" color="text.secondary">
												{t.batches.fermentationDuration}
											</Typography>
											<Box>
												{daysFermenting !== null && (
													<Typography variant="body1" fontWeight={600}>
														{daysFermenting} {daysFermenting === 1 ? t.batches.day : t.batches.days} {t.batches.fermenting}
													</Typography>
												)}
												{batch.expectedFermentationDays !== undefined && (
													<Typography variant="caption" color="text.secondary">
														{t.batches.expected}: {batch.expectedFermentationDays} {batch.expectedFermentationDays === 1 ? t.batches.day : t.batches.days}
													</Typography>
												)}
												{daysRemaining !== null && (
													<Typography 
														variant="caption" 
														color={daysRemaining === 0 ? 'success.main' : daysRemaining <= 3 ? 'warning.main' : 'text.secondary'}
														sx={{ fontWeight: daysRemaining <= 3 ? 600 : 400 }}
													>
														{daysRemaining === 0 
															? `✓ ${t.batches.complete}`
															: `${daysRemaining} ${daysRemaining === 1 ? t.batches.day : t.batches.days} ${t.batches.daysRemaining}`
														}
													</Typography>
												)}
											</Box>
										</Box>
									</Stack>
								</Grid>
							)}
						</Grid>
					</Paper>

					{batch.readings && batch.readings.length > 0 && (() => {
						// Sort readings by time descending (newest first)
						const sortedReadings = [...batch.readings].sort((a, b) => {
							const timeA = a.time || 0;
							const timeB = b.time || 0;
							return timeB - timeA; // Descending order (newest first)
						});
						const displayedReadings = showAllReadings ? sortedReadings : sortedReadings.slice(0, 5);
						
						return (
							<Paper sx={{ p: 3, mb: 3 }}>
								<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
									<Typography variant="h6" fontWeight={700}>
										Readings ({batch.readings.length} total)
									</Typography>
									{batch.readings.length > 5 && (
										<Button
											size="small"
											onClick={() => setShowAllReadings(!showAllReadings)}
											endIcon={showAllReadings ? <ExpandMoreIcon sx={{ transform: 'rotate(180deg)' }} /> : <ExpandMoreIcon />}
										>
											{showAllReadings ? 'Show Less' : `Show All (${batch.readings.length})`}
										</Button>
									)}
								</Box>
								<TableContainer>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell><Typography variant="caption" fontWeight={600}>Time</Typography></TableCell>
												<TableCell align="right"><Typography variant="caption" fontWeight={600}>Temp (°C)</Typography></TableCell>
												<TableCell align="right"><Typography variant="caption" fontWeight={600}>SG</Typography></TableCell>
												<TableCell align="right"><Typography variant="caption" fontWeight={600}>Battery (V)</Typography></TableCell>
												<TableCell align="right"><Typography variant="caption" fontWeight={600}>Signal (dBm)</Typography></TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{displayedReadings.map((reading, index) => {
												// Compare with the previous reading in the sorted array (which is older)
												const previousReading = index < sortedReadings.length - 1 ? sortedReadings[index + 1] : undefined;
												const tempTrend = getTrendIndicator(reading.temp, previousReading?.temp);
												const sgTrend = getTrendIndicator(reading.sg, previousReading?.sg, true); // Lower SG is better during fermentation
												const batteryTrend = getTrendIndicator(reading.battery, previousReading?.battery);
												
												return (
													<TableRow key={index} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
														<TableCell>
															<Typography variant="body2">
																{reading.time ? new Date(reading.time).toLocaleString() : 'N/A'}
															</Typography>
														</TableCell>
														<TableCell align="right">
															<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
																<Typography variant="body2" fontWeight={index === 0 ? 600 : 400}>
																	{isValidNumber(reading.temp) ? reading.temp.toFixed(1) : '—'}
																</Typography>
																{tempTrend && (
																	<Box sx={{ color: tempTrend.color, display: 'flex', alignItems: 'center' }}>
																		{tempTrend.icon}
																	</Box>
																)}
															</Box>
														</TableCell>
														<TableCell align="right">
															<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
																<Typography variant="body2" fontWeight={index === 0 ? 600 : 400}>
																	{isValidNumber(reading.sg) ? reading.sg.toFixed(3) : '—'}
																</Typography>
																{sgTrend && (
																	<Box sx={{ color: sgTrend.color, display: 'flex', alignItems: 'center' }}>
																		{sgTrend.icon}
																	</Box>
																)}
																{index === 0 && batch.estimatedFg !== undefined && batch.estimatedOg !== undefined && batch.status === 'fermenting' && isValidNumber(reading.sg) && (
																	<Tooltip title={`Target FG: ${batch.estimatedFg.toFixed(3)}`}>
																		<Chip 
																			size="small" 
																			label={`${Math.min(100, Math.max(0, ((batch.estimatedOg - reading.sg) / (batch.estimatedOg - batch.estimatedFg)) * 100)).toFixed(0)}%`}
																			color={reading.sg <= batch.estimatedFg ? 'success' : 'default'}
																			sx={{ ml: 0.5, height: 20 }}
																		/>
																	</Tooltip>
																)}
															</Box>
														</TableCell>
														<TableCell align="right">
															<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
																<Typography 
																	variant="body2" 
																	fontWeight={index === 0 ? 600 : 400}
																	color={isValidNumber(reading.battery) && reading.battery < 3.5 ? 'error.main' : 'inherit'}
																>
																	{isValidNumber(reading.battery) ? reading.battery.toFixed(2) : '—'}
																</Typography>
																{batteryTrend && (
																	<Box sx={{ color: batteryTrend.color, display: 'flex', alignItems: 'center' }}>
																		{batteryTrend.icon}
																	</Box>
																)}
															</Box>
														</TableCell>
														<TableCell align="right">
															<Typography 
																variant="body2" 
																fontWeight={index === 0 ? 600 : 400}
																color={isValidNumber(reading.rssi) && reading.rssi < -80 ? 'error.main' : isValidNumber(reading.rssi) && reading.rssi < -70 ? 'warning.main' : 'inherit'}
															>
																{isValidNumber(reading.rssi) ? reading.rssi : '—'}
															</Typography>
														</TableCell>
													</TableRow>
												);
											})}
										</TableBody>
									</Table>
								</TableContainer>
								{batch.readings.length > 5 && !showAllReadings && (
									<Box sx={{ mt: 2, textAlign: 'center' }}>
										<Typography variant="caption" color="text.secondary">
											Showing 5 of {batch.readings.length} readings
										</Typography>
									</Box>
								)}
							</Paper>
						);
					})()}

					<Paper sx={{ p: 3, mb: 3 }}>
						<Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
							{t.batches.measurements}
						</Typography>
						<Grid container spacing={2}>
							{(isNumber(batch.originalGravity) || isNumber(batch.estimatedOg)) && (
								<Grid item xs={6} sm={4}>
									<Typography variant="caption" color="text.secondary">
										{t.batches.originalGravity}
									</Typography>
									{isNumber(batch.originalGravity) ? (
										<Box>
											<Typography variant="h6" fontWeight={700}>
												{batch.originalGravity.toFixed(3)}
											</Typography>
											{isNumber(batch.estimatedOg) && (
												<Typography variant="caption" color="text.secondary">
													{t.batches.estimated}: {batch.estimatedOg.toFixed(3)}
													{(() => {
														const diff = calculatePercentageDiff(batch.originalGravity, batch.estimatedOg);
														return diff !== null && (
															<span style={{ color: diff > 0 ? 'green' : diff < 0 ? 'red' : 'inherit' }}>
																{' '}({formatPercentage(diff)})
															</span>
														);
													})()}
												</Typography>
											)}
										</Box>
									) : isNumber(batch.estimatedOg) ? (
										<Typography variant="h6" fontWeight={700} color="text.secondary">
											{batch.estimatedOg.toFixed(3)} ({t.batches.estimated})
										</Typography>
									) : null}
								</Grid>
							)}
							{(isNumber(batch.finalGravity) || isNumber(batch.estimatedFg)) && (
								<Grid item xs={6} sm={4}>
									<Typography variant="caption" color="text.secondary">
										{t.batches.finalGravity}
									</Typography>
									{isNumber(batch.finalGravity) ? (
										<Box>
											<Typography variant="h6" fontWeight={700}>
												{batch.finalGravity.toFixed(3)}
											</Typography>
											{isNumber(batch.estimatedFg) && (
												<Typography variant="caption" color="text.secondary">
													{t.batches.estimated}: {batch.estimatedFg.toFixed(3)}
													{(() => {
														const diff = calculatePercentageDiff(batch.finalGravity, batch.estimatedFg);
														return diff !== null && (
															<span style={{ color: diff > 0 ? 'green' : diff < 0 ? 'red' : 'inherit' }}>
																{' '}({formatPercentage(diff)})
															</span>
														);
													})()}
												</Typography>
											)}
										</Box>
									) : isNumber(batch.estimatedFg) ? (
										<Typography variant="h6" fontWeight={700} color="text.secondary">
											{batch.estimatedFg.toFixed(3)} ({t.batches.estimated})
										</Typography>
									) : null}
								</Grid>
							)}
							{(isNumber(batch.abv) || isNumber(batch.estimatedAbv)) && (
								<Grid item xs={6} sm={4}>
									<Typography variant="caption" color="text.secondary">
										ABV
									</Typography>
									{isNumber(batch.abv) ? (
										<Box>
											<Typography variant="h6" fontWeight={700}>
												{batch.abv.toFixed(1)}%
											</Typography>
											{isNumber(batch.estimatedAbv) && (
												<Typography variant="caption" color="text.secondary">
													{t.batches.estimated}: {batch.estimatedAbv.toFixed(1)}%
													{(() => {
														const diff = calculatePercentageDiff(batch.abv, batch.estimatedAbv);
														return diff !== null && (
															<span style={{ color: diff > 0 ? 'green' : diff < 0 ? 'red' : 'inherit' }}>
																{' '}({formatPercentage(diff)})
															</span>
														);
													})()}
												</Typography>
											)}
										</Box>
									) : isNumber(batch.estimatedAbv) ? (
										<Typography variant="h6" fontWeight={700} color="text.secondary">
											{batch.estimatedAbv.toFixed(1)}% ({t.batches.estimated})
										</Typography>
									) : null}
								</Grid>
							)}
							{(isNumber(batch.ibu) || isNumber(batch.estimatedIbu)) && (
								<Grid item xs={6} sm={4}>
									<Typography variant="caption" color="text.secondary">
										IBU
									</Typography>
									{isNumber(batch.ibu) ? (
										<Box>
											<Typography variant="h6" fontWeight={700}>
												{batch.ibu}
											</Typography>
											{isNumber(batch.estimatedIbu) && (
												<Typography variant="caption" color="text.secondary">
													{t.batches.estimated}: {batch.estimatedIbu}
													{(() => {
														const diff = calculatePercentageDiff(batch.ibu, batch.estimatedIbu);
														return diff !== null && (
															<span style={{ color: diff > 0 ? 'green' : diff < 0 ? 'red' : 'inherit' }}>
																{' '}({formatPercentage(diff)})
															</span>
														);
													})()}
												</Typography>
											)}
										</Box>
									) : isNumber(batch.estimatedIbu) ? (
										<Typography variant="h6" fontWeight={700} color="text.secondary">
											{batch.estimatedIbu} ({t.batches.estimated})
										</Typography>
									) : null}
								</Grid>
							)}
							{(isNumber(batch.srm) || isNumber(batch.estimatedColor)) && (
								<Grid item xs={6} sm={4}>
									<Typography variant="caption" color="text.secondary">
										SRM
									</Typography>
									{isNumber(batch.srm) ? (
										<Box>
											<Typography variant="h6" fontWeight={700}>
												{batch.srm}
											</Typography>
											{isNumber(batch.estimatedColor) && (
												<Typography variant="caption" color="text.secondary">
													{t.batches.estimated}: {batch.estimatedColor.toFixed(1)}
													{(() => {
														const diff = calculatePercentageDiff(batch.srm, batch.estimatedColor);
														return diff !== null && (
															<span style={{ color: diff > 0 ? 'green' : diff < 0 ? 'red' : 'inherit' }}>
																{' '}({formatPercentage(diff)})
															</span>
														);
													})()}
												</Typography>
											)}
										</Box>
									) : isNumber(batch.estimatedColor) ? (
										<Typography variant="h6" fontWeight={700} color="text.secondary">
											{batch.estimatedColor.toFixed(1)} ({t.batches.estimated})
										</Typography>
									) : null}
								</Grid>
							)}
							{isNumber(batch.measuredAttenuation) && (
								<Grid item xs={6} sm={4}>
									<Typography variant="caption" color="text.secondary">
										{t.batches.attenuation}
									</Typography>
									<Typography variant="h6" fontWeight={700}>
										{batch.measuredAttenuation.toFixed(1)}%
									</Typography>
								</Grid>
							)}
							{isNumber(batch.measuredEfficiency) && (
								<Grid item xs={6} sm={4}>
									<Typography variant="caption" color="text.secondary">
										{t.batches.efficiency}
									</Typography>
									<Typography variant="h6" fontWeight={700}>
										{batch.measuredEfficiency.toFixed(1)}%
									</Typography>
								</Grid>
							)}
						</Grid>
					</Paper>

					{batch.notes && batch.notes.length > 0 && (
						<Paper sx={{ p: 3, mb: 3 }}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
								<NotesIcon color="primary" />
								<Typography variant="h6" fontWeight={700}>
									{t.batches.notes}
								</Typography>
							</Box>
							<List>
								{batch.notes.map((note, index) => (
									<ListItem key={index} sx={{ px: 0 }}>
										<ListItemIcon>
											<NotesIcon fontSize="small" color="action" />
										</ListItemIcon>
										<ListItemText
											primary={note.note}
											secondary={new Date(note.timestamp).toLocaleString()}
										/>
									</ListItem>
								))}
							</List>
						</Paper>
					)}

					{batch.events && batch.events.length > 0 && (
						<Paper sx={{ p: 3 }}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
								<EventIcon color="primary" />
								<Typography variant="h6" fontWeight={700}>
									{t.batches.events}
								</Typography>
							</Box>
							<List>
								{batch.events
									.sort((a, b) => b.time - a.time)
									.map((event, index) => (
										<ListItem key={index} sx={{ px: 0 }}>
											<ListItemIcon>
												<EventIcon fontSize="small" color={event.active ? 'primary' : 'action'} />
											</ListItemIcon>
											<ListItemText
												primary={event.title}
												secondary={
													<Box>
														<Typography variant="caption" component="span" display="block">
															{formatEventDate(event.time)}
														</Typography>
														{event.description && (
															<Typography variant="caption" component="span" color="text.secondary">
																{event.description}
															</Typography>
														)}
													</Box>
												}
											/>
											{event.active && (
												<Chip label={t.batches.active} size="small" color="primary" />
											)}
										</ListItem>
									))}
							</List>
						</Paper>
					)}
				</Grid>

				<Grid item xs={12} md={4}>
					<Stack spacing={3}>
						{/* Scheduled AI Analysis */}
						<Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
								<ScheduleIcon color="primary" />
								<Typography variant="h6" fontWeight={700}>
									Schedule Analysis
								</Typography>
								{schedule && (
									<Chip 
										size="small" 
										label={schedule.enabled ? 'Active' : 'Disabled'} 
										color={schedule.enabled ? 'success' : 'default'}
									/>
								)}
							</Box>
							{scheduleLoading ? (
								<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
									<CircularProgress size={24} />
								</Box>
							) : (
								<Stack spacing={2}>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
										<TextField
											label="Every"
											type="number"
											value={scheduleInterval}
											onChange={(e) => {
												const value = parseInt(e.target.value) || 1;
												setScheduleInterval(Math.min(24, Math.max(1, value)));
											}}
											inputProps={{ min: 1, max: 24 }}
											size="small"
											sx={{ width: 100 }}
										/>
										<FormControl size="small" sx={{ minWidth: 100 }}>
											<Select
												value={scheduleIntervalUnit}
												onChange={(e) => setScheduleIntervalUnit(e.target.value as 'hours' | 'days')}
											>
												<MenuItem value="hours">Hours</MenuItem>
												<MenuItem value="days">Days</MenuItem>
											</Select>
										</FormControl>
									</Box>
									<FormControlLabel
										control={
											<Switch
												checked={scheduleEnabled}
												onChange={(e) => setScheduleEnabled(e.target.checked)}
											/>
										}
										label={scheduleEnabled ? 'Enabled' : 'Disabled'}
									/>
									<Button
										variant="contained"
										fullWidth
										onClick={handleSaveSchedule}
										disabled={scheduleSaving || scheduleInterval < 1 || scheduleInterval > 24}
										startIcon={<ScheduleIcon />}
										size="small"
									>
										{scheduleSaving ? 'Saving...' : schedule ? 'Update' : 'Create Schedule'}
									</Button>
									{schedule && (
										<Button
											variant="outlined"
											fullWidth
											color="error"
											onClick={handleDeleteSchedule}
											disabled={scheduleSaving}
											startIcon={<DeleteIcon />}
											size="small"
										>
											Delete
										</Button>
									)}
								</Stack>
							)}
						</Paper>

						{/* AI Assistant */}
						<Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
							<Stack spacing={2}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									<PsychologyIcon color="primary" />
									<Typography variant="h6" fontWeight={700}>
										AI Assistant
									</Typography>
								</Box>
								<Typography variant="body2" color="text.secondary">
									Ask questions about this batch and get AI-powered insights about your home brewing process.
								</Typography>
								{/* Review summary */}
								<Box sx={{ py: 1, px: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
									<Typography variant="caption" color="text.secondary" display="block">
										AI reviews
									</Typography>
									{analysesLoading ? (
										<Typography variant="body2">Loading…</Typography>
									) : analyses.length === 0 ? (
										<Typography variant="body2">No reviews yet. Run a review to track progress.</Typography>
									) : (
										<>
											<Typography variant="body2" fontWeight={600}>
												{analyses.length} {analyses.length === 1 ? 'review' : 'reviews'}
											</Typography>
											{analyses[0]?.createdAt && (
												<Typography variant="caption" color="text.secondary">
													Last: {new Date(analyses[0].createdAt).toLocaleString()}
												</Typography>
											)}
										</>
									)}
								</Box>
								<Button
									variant="contained"
									fullWidth
									startIcon={<AutoAwesomeIcon />}
									onClick={handleRunAIReview}
									disabled={analyzing}
								>
									{analyzing ? 'Analyzing...' : 'Run AI Review'}
								</Button>
								<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
									Use the AI chat (bottom-right) to ask questions about this batch anytime.
								</Typography>
							</Stack>
						</Paper>
					</Stack>
				</Grid>
			</Grid>


			{/* AI analyses history – progress over time */}
			<Box sx={{ mt: 3, minWidth: 0, overflow: 'hidden' }}>
				<Paper sx={{ p: { xs: 2, sm: 3 }, overflow: 'hidden', maxWidth: '100%' }}>
					<Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
						<AutoAwesomeIcon color="primary" />
						<Typography variant="h6" fontWeight={700}>
							AI analyses
						</Typography>
						{analyses.length > 0 && (
							<Chip size="small" label={`${analyses.length} ${analyses.length === 1 ? 'analysis' : 'analyses'}`} />
						)}
					</Box>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2, wordBreak: 'break-word' }}>
						All AI reviews for this batch. Use them to track how the batch is progressing over time.
					</Typography>
					{analysesLoading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
							<CircularProgress size={24} />
						</Box>
					) : analyses.length === 0 ? (
						<Typography variant="body2" color="text.secondary">
							No AI analyses yet. Run &quot;Run AI Review&quot; above to get the first one.
						</Typography>
					) : (
						<Stack spacing={1} sx={{ minWidth: 0 }}>
							{displayedAnalyses.map((a) => {
								const isExpanded = expandedAnalysisId === a.analysisId;
								return (
									<Accordion
										key={a.analysisId}
										expanded={isExpanded}
										onChange={() => setExpandedAnalysisId(isExpanded ? null : a.analysisId)}
										sx={{ '&:before': { display: 'none' }, boxShadow: 'none', border: '1px solid', borderColor: 'divider', minWidth: 0 }}
									>
										<AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 56 }}>
											<Stack
												direction={{ xs: 'column', sm: 'row' }}
												alignItems={{ xs: 'flex-start', sm: 'center' }}
												spacing={1}
												sx={{ width: '100%', minWidth: 0 }}
											>
												<Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
													<EventIcon fontSize="small" color="action" />
													<Typography variant="body2" color="text.secondary">
														{new Date(a.createdAt).toLocaleString()}
													</Typography>
												</Stack>
												<Typography
													variant="body2"
													sx={{
														flex: 1,
														minWidth: 0,
														wordBreak: 'break-word',
														overflowWrap: 'break-word'
													}}
												>
													{a.summary.length > 100 ? `${a.summary.slice(0, 100).trim()}…` : a.summary}
												</Typography>
											</Stack>
										</AccordionSummary>
										<AccordionDetails sx={{ pt: 0, minWidth: 0 }}>
											<Stack spacing={2} sx={{ minWidth: 0 }}>
												<Box sx={{ minWidth: 0 }}>
													<Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>Summary</Typography>
													<Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{a.summary}</Typography>
												</Box>
												{a.recommendations && a.recommendations.length > 0 && (
													<Box sx={{ minWidth: 0 }}>
														<Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>Recommendations</Typography>
														<List dense disablePadding>
															{a.recommendations.map((rec, i) => (
																<ListItem key={i} sx={{ py: 0, px: 0 }}>
																	<ListItemIcon sx={{ minWidth: 28 }}>
																		<CheckCircleIcon fontSize="small" color="success" />
																	</ListItemIcon>
																	<ListItemText primary={rec} primaryTypographyProps={{ variant: 'body2', sx: { wordBreak: 'break-word', overflowWrap: 'break-word' } }} />
																</ListItem>
															))}
														</List>
													</Box>
												)}
												{a.changes && a.changes.length > 0 && (
													<Box sx={{ minWidth: 0 }}>
														<Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>Suggested changes</Typography>
														<Stack spacing={1}>
															{a.changes.map((c, i) => (
																<Box key={i} sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, minWidth: 0 }}>
																	<Typography variant="caption" fontWeight={600} sx={{ wordBreak: 'break-word' }}>{c.field}</Typography>
																	<Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{c.oldValue?.toString()} → {c.newValue?.toString()}</Typography>
																	<Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', wordBreak: 'break-word' }}>{c.reason}</Typography>
																</Box>
															))}
														</Stack>
													</Box>
												)}
											</Stack>
										</AccordionDetails>
									</Accordion>
								);
							})}
							{hasMoreAnalyses && (
								<Button
									size="small"
									variant="outlined"
									onClick={() => setShowAllAnalyses((prev) => !prev)}
									startIcon={showAllAnalyses ? <ExpandMoreIcon sx={{ transform: 'rotate(180deg)' }} /> : <ExpandMoreIcon />}
									sx={{ alignSelf: 'flex-start', mt: 0.5 }}
								>
									{showAllAnalyses
										? 'Show less'
										: `Show ${analyses.length - AI_ANALYSES_VISIBLE} more`}
								</Button>
							)}
						</Stack>
					)}
				</Paper>
			</Box>

			{aiReview && (
				<Box sx={{ mt: 3, minWidth: 0, overflow: 'hidden' }}>
					<Paper sx={{ p: { xs: 2, sm: 3 }, overflow: 'hidden', maxWidth: '100%' }}>
						<Stack spacing={3}>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
								<AutoAwesomeIcon color="primary" />
								<Typography variant="h6" fontWeight={700}>
									AI Review Results
								</Typography>
							</Box>

							{/* Summary */}
							<Box sx={{ minWidth: 0 }}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
									<AlertIcon color="primary" fontSize="small" />
									<Typography variant="subtitle1" fontWeight={600}>
										Summary
									</Typography>
								</Box>
								<Typography variant="body1" color="text.secondary" sx={{ pl: { xs: 0, sm: 4 }, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
									{aiReview.summary}
								</Typography>
							</Box>

							{/* Recommendations */}
							{aiReview.recommendations && aiReview.recommendations.length > 0 && (
								<Box sx={{ minWidth: 0 }}>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
										<CheckCircleIcon color="primary" fontSize="small" />
										<Typography variant="subtitle1" fontWeight={600}>
											Recommendations
										</Typography>
									</Box>
									<List sx={{ pl: { xs: 0, sm: 4 } }}>
										{aiReview.recommendations.map((rec, index) => (
											<ListItem key={index} sx={{ py: 0.5, px: 0 }}>
												<ListItemIcon sx={{ minWidth: 32 }}>
													<CheckCircleIcon fontSize="small" color="success" />
												</ListItemIcon>
												<ListItemText primary={rec} primaryTypographyProps={{ sx: { wordBreak: 'break-word', overflowWrap: 'break-word' } }} />
											</ListItem>
										))}
									</List>
								</Box>
							)}

							{/* Changes */}
							{aiReview.changes && aiReview.changes.length > 0 && (
								<Box sx={{ minWidth: 0 }}>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
										<EventIcon color="primary" fontSize="small" />
										<Typography variant="subtitle1" fontWeight={600}>
											Updated Values
										</Typography>
									</Box>
									<Accordion sx={{ '&:before': { display: 'none' } }}>
										<AccordionSummary expandIcon={<ExpandMoreIcon />}>
											<Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
												{aiReview.changes.length} {aiReview.changes.length === 1 ? 'change' : 'changes'} detected
											</Typography>
										</AccordionSummary>
										<AccordionDetails sx={{ pt: 0 }}>
											<Stack spacing={2}>
												{aiReview.changes.map((change, index) => (
													<Box key={index} sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'background.default', borderRadius: 1, minWidth: 0 }}>
														<Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, wordBreak: 'break-word' }}>
															{change.field}
														</Typography>
														<Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0.5, sm: 2 }, mb: 1 }}>
															<Box sx={{ minWidth: 0 }}>
																<Typography variant="caption" color="text.secondary">
																	Previous:
																</Typography>
																<Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
																	{change.oldValue?.toString() || 'N/A'}
																</Typography>
															</Box>
															<Box sx={{ minWidth: 0 }}>
																<Typography variant="caption" color="text.secondary">
																	Updated:
																</Typography>
																<Typography variant="body2" fontWeight={600} color="primary.main" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
																	{change.newValue?.toString() || 'N/A'}
																</Typography>
															</Box>
														</Box>
														<Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', wordBreak: 'break-word' }}>
															{change.reason}
														</Typography>
													</Box>
												))}
											</Stack>
										</AccordionDetails>
									</Accordion>
								</Box>
							)}
						</Stack>
					</Paper>
				</Box>
			)}

			{batch && (
				<AIChatOverlay
					title="AI Assistant"
					subtitle={`Batch: ${batch.name}. Recipe: ${recipe?.name}`}
					historyKey={batch.id}
					onLoginAgain={() => navigate('/login')}
					placeholder={t.batches.askAIAboutBatch || 'Ask about this batch...'}
					welcomeText="Ask me anything about this batch. I can help with fermentation, gravity readings, ABV, and more."
					headerExtra={<CreditsDisplay variant="chip" size="small" />}
					loadHistory={async () => {
						const userId = getUserId();
						const authHeader = getAuthHeader();
						if (!userId || !authHeader) return [];
						const { items = [] } = await AIService.getQuestionHistory(userId, authHeader, batch.id);
						// Backend returns newest first; sort ascending so chat shows chronological order (oldest first).
						const sorted = [...items].sort((a, b) => {
							const ta = typeof a.createdAt === 'number' ? a.createdAt : new Date(a.createdAt).getTime();
							const tb = typeof b.createdAt === 'number' ? b.createdAt : new Date(b.createdAt).getTime();
							return ta - tb;
						});
						const loaded: ChatMessage[] = [];
						for (const item of sorted) {
							loaded.push({
								id: item.id,
								role: 'user',
								content: item.question,
								timestamp: new Date(typeof item.createdAt === 'number' ? item.createdAt : item.createdAt)
							});
							if (item.answer) {
								loaded.push({
									id: `${item.id}-reply`,
									role: 'assistant',
									content: item.answer,
									timestamp: new Date(typeof item.createdAt === 'number' ? item.createdAt : item.createdAt)
								});
							}
						}
						return loaded;
					}}
					onAsk={async (question) => {
						const userId = getUserId();
						const authHeader = getAuthHeader();
						if (!userId) throw new Error('Please log in again.');
						setCreditsLoading(true);
						try {
							const response: any = await AIService.askQuestionAboutBatch(userId, question, batch, authHeader);
							// Handle both direct { answer } and API Gateway-wrapped { body: string } shapes
							let answer: string | undefined = response?.answer;
							if (typeof answer !== 'string' && response?.body) {
								const parsed = typeof response.body === 'string' ? JSON.parse(response.body) : response.body;
								answer = parsed?.answer;
							}
							if (typeof answer !== 'string') throw new Error('Invalid response from AI');
							return answer;
						} finally {
							await loadCredits();
							setCreditsLoading(false);
						}
					}}
					suggestedQuestions={batch ? getSuggestedQuestionsForStatus(batch.status) : []}
				/>
			)}
		</Container>
	);
};

export default BatchDetailPage;
